import { IpaType } from "../../phonemes/ipa";
import { englishPhonotactics } from "../../phonotactics/englishPhonotactics";
import { lexicalSets } from "../../rules/findLexicalSets";
import { AddLengthRule, LengthRules } from "../../rules/lengthRules";
import { SetRules } from "../../rules/replaceLexicalSets";
import { Rule } from "../../rules/rule";
import { RuleSet, RuleType } from "../../rules/ruleSet";
import { isRuleParsingError, stringToLengthAdds, stringToRule } from "../../rules/serializeRule";

/** reverse ruleSetToString, making lots of simplifying assumptions */
export function ruleSetFromString(text: string): RuleSet | string {
  let ruleSet: RuleSet = {};
  const lines = text.split('\n').map(line => line.trim());
  for (let i = 1; i < lines.length - 1; i++) {
    const line = lines[i];
    const colon = line.search(':');
    if (colon === -1) {
      return 'expected "prop:" on line ' + i.toString();
    }
    const prop = line.substring(0, colon);
    if (prop === 'lexicalSets') {
      const result = lexicalSetsFromStrings(lines, i + 1);
      if (typeof result === 'string') {
        return result + ', lexicalSets started at line ' + i.toString();
      }
      ruleSet = { ...ruleSet, lexicalSets: result[0] };
      i = result[1];
    } else if (prop === 'phonemeLengths') {
      const result = phonemeLengthsFromStrings(lines, i + 1);
      if (typeof result === 'string') {
        return result + ', phonemeLengths started at line ' + i.toString();
      }
      ruleSet = { ...ruleSet, phonemeLengths: result[0] };
      i = result[1];
    } else if (prop === 'rules' || prop === 'rules2') {
      const result = rulesFromStrings(lines, i + 1);
      if (typeof result === 'string') {
        return result + ', rules started at line ' + i.toString();
      }
      if (prop === 'rules') {
        ruleSet = { ...ruleSet, rules: result[0] };
      } else {
        ruleSet = { ...ruleSet, rules2: result[0] };
      }
      i = result[1];
    } else {
      const other = otherFromStrings(prop, line.substring(colon + 1, line.length - 1));
      if (typeof other === 'string') {
        return other + ' at line ' + i.toString();
      }
      ruleSet = { ...ruleSet, ...other };
    }
  }
  return ruleSet;
}

// e.g. FACE: 'eɪ', PRICE: 'aɪ',
function lexicalSetsFromStrings(
  lines: string[], start: number
): [SetRules, number] | string {
  const end = findLine(lines, start, '},');
  if (end === null) {
    return 'unmatched lexicalSet';
  }

  // parse
  let sets: Record<string, string> = {};
  for (let i = start; i < end; i++) {
    const pieces = lines[i].split(',');
    pieces.forEach(piece => {
      const result = addToRecord(piece, sets);
      if (typeof result === 'string') {
        return result + ' at line ' + i.toString();
      }
    });
  }

  // verify that all lexical sets are present
  const missing: string[] = [];
  lexicalSets.forEach(item => {
    if (sets[item.name] === undefined) {
      missing.push(item.name);
    }
  });
  if (missing.length) {
    return 'missing lexical sets ' + JSON.stringify(missing);
  }

  return [sets as SetRules, end];
}

/** parse the entire phonemeLengths object */
function phonemeLengthsFromStrings(
  lines: string[], start: number
): [LengthRules, number] | string {
  // parse `map: {}`
  const mapStart = findLine(lines, start, 'map: {');
  if (mapStart === null) {
    return 'missing phonemeLengths.map';
  }
  const result = lengthMapFromStrings(lines, mapStart + 1);
  if (typeof result === 'string') {
    return result;
  }
  const [map, endMap] = result;

  // parse `add: []` if present
  const addStart = findLine(lines, endMap, 'adds: [');
  const result2 = addStart ? lengthAddsFromStrings(lines, addStart + 1) : undefined;
  if (typeof result2 === 'string') {
    return result2;
  }
  const [adds, endPieces]: [AddLengthRule[] | undefined, number] =
    result2 === undefined ? [undefined, endMap] : result2;

  // find end of entire object
  const end = findLine(lines, endPieces + 1, '},');
  if (end === null) {
    return 'unmatched phonemeLengths';
  }
  return adds ? [{ map, adds }, end] : [{ map }, end];
}

/** parse the map of phonemes-to-length */
function lengthMapFromStrings(
  lines: string[], start: number
): [Record<string, number>, number] | string {
  const end = findLine(lines, start, '},');
  if (end === null) {
    return 'unmatched phonemeLengths.map';
  }

  // parse
  const lengths: Record<string, number> = {};
  for (let i = start; i < end; i++) {
    const pieces = lines[i].split(',');
    pieces.forEach(piece => {
      const result = addNumbersToRecord(piece, lengths);
      if (typeof result === 'string') {
        return result + ' at line ' + i.toString();
      }
    });
  }

  return [lengths, end];
}

/** parse the rules that optionally add to phoneme lengths */
function lengthAddsFromStrings(
  lines: string[], start: number
): [AddLengthRule[], number] | string {
  const end = findLine(lines, start, '],');
  if (end === null) {
    return 'unmatched phonemeLengths.add';
  }

  const rules: AddLengthRule[] = [];
  for (let i = start; i < end; i++) {
    const line = lines[i];
    if (line[0] === '{') {
      // parse rule as JSON
      try {
        const rule = JSON.parse(line) as unknown as AddLengthRule;
        if (rule.addMs === undefined || typeof rule.addMs !== 'number') {
          return 'phonemeLengths.adds missing addMs at line' + i.toString();
        }
        rules.push(rule);
      } catch (error) {
        return (error as Object).toString();
      }
    } else {
      // parse shortened form
      const rule = stringToLengthAdds(line);
      if (typeof rule === 'string') {
        return rule;
      }
      rules.push(rule);
    }
  }
  return [rules, end];
}

// from: 'ð', to: 'v', next: ["#word"],
function rulesFromStrings(
  lines: string[], start: number
): [RuleType[], number] | string {
  const end = findLine(lines, start, '],');
  if (end === null) {
    return 'unmatched rules';
  }

  const rules: RuleType[] = [];
  for (let i = start; i < end; i++) {
    const line = lines[i].trim();
    if (line.startsWith('from')) {
      // long form
      const sets: Record<string, string> = {};
      const pieces = splitRuleString(lines[i].trim());
      pieces.forEach(piece => {
        if (piece !== null) {
          const result = addToRecord(piece, sets);
          if (typeof result === 'string') {
            return result + ' at line ' + i.toString();
          }
        }
      });
      if (sets['from'] === undefined || sets['to'] === undefined) {
        return 'expected "from" & "to" in ' + lines[i] + ' at line ' + i.toString();
      }
      rules.push(sets as unknown as Rule);
    } else {
      // short form
      const shortForm = stringToRule(lines[i]);
      if (!isRuleParsingError(shortForm)) {
        rules.push(shortForm);
      } else {
        return `missing ${shortForm.missing}`;
      }
    }
  }

  return [rules, end];
}

// assume ordering -- from: 'ð', to: 'v', previous: 'a', next: ["#word"],
function splitRuleString(str: string): (string | null)[] {
  const toStart = str.search('to:');
  const prevStart = str.search('previous:');
  const nextStart = str.search('next:');
  if (toStart === -1) {
    return [];
  }
  const from = str.substring(0, toStart);
  const toEnd = prevStart !== -1 ? prevStart : nextStart !== -1 ? nextStart : str.length;
  const to = str.substring(toStart, toEnd);
  const prev = prevStart !== -1 ? str.substring(prevStart, nextStart !== -1 ? nextStart : str.length) : null;
  const next = nextStart !== -1 ? str.substring(nextStart) : null;
  return [clean(from), clean(to), clean(prev), clean(next)];
}

function clean(s: string | null): string | null {
  if (s === null) {
    return s;
  }
  s = s.trim();
  return s[s.length - 1] === ',' ? s.substring(0, s.length - 1) : s;
}

/** parse contents as JSON */
function otherFromStrings(prop: string, toParse: string): RuleSet | string {
  if (prop === 'fsm') {
    const trimmed = toParse.trim();
    const fsm = trimmed.substring(1, trimmed.length - 1);
    return { fsm };
  } else if (prop === 'syllableBreaks') {
    // currently hard-coded
    return { syllableBreaks: englishPhonotactics };
  }

  try {
    const obj = JSON.parse(toParse);
    switch (prop) {
      case 'preprocess': return { preprocess: obj } as RuleSet;
      case 'useRPDictionary': return { useRPDictionary: obj } as RuleSet;
      case 'wholeWords': return { wholeWords: obj as Record<string, IpaType> };
    }
    return 'unknown property: ' + prop;
  } catch (error) {
    return (error as Object).toString();
  }
}

/** from lines[start], search for a line that starts w/ 'toFind' */
function findLine(lines: string[], start: number, toFind: string): number | null {
  for (let i = start; i < lines.length; i++) {
    if (lines[i].startsWith(toFind)) {
      return i;
    }
  }
  return null;
}

function addToRecord(str: string, record: Record<string, string | string[]>): string | null {
  const piece = str.trim();
  const colon = piece.search(':');
  if (colon === -1) {
    return 'expected colon in ' + piece;
  }
  const name = piece.substring(0, colon);
  const leftbracket = piece.indexOf('[');
  const rightbracket = piece.indexOf(']');
  if (leftbracket === -1 || rightbracket === -1) {
    // string
    const first = piece.indexOf("'");
    if (first === -1) {
      return 'expected a quote in ' + piece;
    }
    const value = piece.substring(first + 1, piece.length - 1);
    record[name] = value;
  } else {
    // array
    const remainder = piece.substring(leftbracket, rightbracket + 1);
    const array = JSON.parse(remainder) as string[];
    record[name] = array;
  }
  return null;
}

function addNumbersToRecord(str: string, record: Record<string, number>): string | null {
  const piece = str.trim();
  const colon = piece.search(':');
  if (colon === -1) {
    return 'expected colon in ' + piece;
  }
  const name = piece.substring(0, colon);
  const value = piece.substring(colon + 1, piece.length);
  record[name] = Number.parseFloat(value);
  return null;
}

/*
{
lexicalSets: {
  FACE: 'eɪ', PRICE: 'aɪ', GOAT: 'oʊ', CHOICE: 'ɔɪ', MOUTH: 'aʊ',
  START: 'ɑɹ', SQUARE: 'ɛɹ', NEAR: 'ɪɹ', NORTH: 'ɔɹ', FORCE: 'ɔɹ',
  CURE: 'ʊɹ', lettER: 'ɝ', NURSE: 'ɝ',
  PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɔ', CLOTH: 'ɔ', TRAP: 'æ', BATH: 'æ',
  DRESS: 'ɛ', FLEECE: 'i', happY: 'i', KIT: 'ɪ', GOOSE: 'u', FOOT: 'ʊ',
  STRUT: 'ə', commA: 'ə',
},
rules: [
  from: 'θ', to: 'f',
  from: 'ð', to: 'v', next: ["#word"],
  from: 'ð', to: 'v', previous: ["vwl"], next: ["vwl"],
],
}
*/
